import React from 'react';
import { ClipboardList, Clock, Award, AlertTriangle, PlayCircle, Calendar, FileText, Lock } from 'lucide-react';
import { Badge } from '../ui/badge';

const AssessmentItem = ({ assessment, onAssessmentClick, locked = false, lockReason = '', onLockedClick }) => {
  const getStatusConfig = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'evaluated':
        return {
          label: 'Graded',
          color: 'bg-emerald-500 text-white border-emerald-400 shadow-sm shadow-emerald-200',
          icon: Award
        };
      case 'submitted':
        return {
          label: 'In Review',
          color: 'bg-indigo-500 text-white border-indigo-400 shadow-sm shadow-indigo-200',
          icon: ClipboardList
        };
      case 'late':
        return {
          label: 'Past Due',
          color: 'bg-rose-500 text-white border-rose-400 shadow-sm shadow-rose-200',
          icon: AlertTriangle
        };
      default:
        return {
          label: 'Active',
          color: 'bg-amber-500 text-white border-amber-400 shadow-sm shadow-amber-200',
          icon: PlayCircle
        };
    }
  };

  const formatDate = (value) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  const formatRemaining = (seconds) => {
    if (seconds == null) return '--';
    if (seconds <= 0) return 'Deadline passed';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} left`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} left`;
    return 'Final hour';
  };

  const statusConfig = getStatusConfig(assessment.status);
  const StatusIcon = statusConfig.icon;

  const handleClick = () => {
    if (locked) {
      onLockedClick?.(lockReason);
      return;
    }
    onAssessmentClick?.(assessment);
  };

  const getCardStyle = () => {
    if (locked) return 'border-slate-200 bg-slate-50/50 opacity-60 grayscale-[0.2] cursor-not-allowed';

    switch ((assessment.status || '').toLowerCase()) {
      case 'evaluated':
        return 'border-emerald-200 bg-emerald-50/40 hover:border-emerald-400 hover:bg-emerald-50/60 shadow-inner shadow-emerald-100/40';
      case 'submitted':
        return 'border-indigo-200 bg-indigo-50/40 hover:border-indigo-400 hover:bg-indigo-50/60 shadow-inner shadow-indigo-100/40';
      case 'late':
        return 'border-rose-200 bg-rose-50/40 hover:border-rose-400 hover:bg-rose-50/60 shadow-inner shadow-rose-100/40';
      default:
        return 'border-amber-200 bg-amber-50/40 hover:border-amber-400 hover:bg-amber-50/60 shadow-inner shadow-amber-100/40';
    }
  };

  return (
    <div
      className={`group p-2.5 md:p-4 rounded-2xl border transition-all duration-300 cursor-pointer w-full min-w-0 ${getCardStyle()}`}
      onClick={handleClick}
    >
      <div className="flex items-start justify-between gap-2.5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 min-w-0 flex-1 w-full overflow-hidden">
            <div className={`p-1.5 rounded-lg shrink-0 ${locked ? 'bg-slate-200 text-slate-500' :
                assessment.status === 'evaluated' ? 'bg-emerald-100 text-emerald-600' :
                  assessment.status === 'submitted' ? 'bg-indigo-100 text-indigo-600' :
                    assessment.status === 'late' ? 'bg-rose-100 text-rose-600' :
                      'bg-amber-100 text-amber-600'
              }`}>
              <StatusIcon className="h-3 w-3 md:h-3.5 md:w-3.5" />
            </div>
            <h4 className="flex-1 min-w-0 text-[12px] md:text-[13px] font-bold text-slate-800 line-clamp-1 break-all tracking-tight">
              {assessment.title || 'Untitled Assessment'}
            </h4>
          </div>

          <p className="text-[10px] md:text-[11px] text-slate-500 font-medium line-clamp-2 mb-2.5 break-words leading-tight">
            {assessment.description || assessment.instructions || 'Full assessment content available on launch.'}
          </p>

          <div className="flex flex-wrap items-center gap-2 md:gap-4 text-[9px] md:text-[10px] text-slate-500 font-bold uppercase tracking-wider">
            {assessment.max_marks && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-white/50 rounded-md border border-slate-100 shrink-0">
                <Award className="h-2.5 w-2.5 text-indigo-500" />
                <span>{assessment.max_marks} M</span>
              </span>
            )}
            {assessment.end_date && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-white/50 rounded-md border border-slate-100 shrink-0">
                <Calendar className="h-2.5 w-2.5 text-rose-500" />
                <span>Due {formatDate(assessment.end_date).split(',')[0]}</span>
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          <Badge className={`flex items-center gap-1 ${statusConfig.color} text-[8px] md:text-[9px] font-black border-none uppercase tracking-widest px-2 py-0.5 rounded-full`}>
            {statusConfig.label}
          </Badge>

          {assessment.marks_awarded !== undefined && assessment.max_marks && (
            <div className="bg-white/80 backdrop-blur-sm border border-emerald-100 px-2 py-1 rounded-lg">
              <div className="text-xs font-black text-slate-800 flex items-baseline justify-center gap-0.5 leading-none">
                <span className="text-emerald-600">{assessment.marks_awarded}</span>
                <span className="text-slate-400 text-[9px]">/{assessment.max_marks}</span>
              </div>
            </div>
          )}

          {locked && (
            <div className="text-[8px] font-black text-amber-600 uppercase tracking-tighter bg-amber-50 px-1.5 py-0.5 rounded-md border border-amber-100 flex items-center gap-1">
              <Lock className="w-2.5 h-2.5" />
              Locked
            </div>
          )}

          {assessment.remaining_seconds !== undefined && assessment.status !== 'evaluated' && assessment.status !== 'submitted' && (
            <div className="flex items-center gap-1 text-[8px] font-black text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded-md uppercase tracking-tighter border border-rose-100">
              <Clock className="w-2.5 h-2.5" />
              {formatRemaining(assessment.remaining_seconds)}
            </div>
          )}
        </div>
      </div>

      {lockReason && locked && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-200/50 flex items-center gap-2">
          <div className="w-1 h-1 bg-amber-400 rounded-full animate-pulse" />
          <p className="text-[9px] text-amber-600 font-bold uppercase tracking-widest leading-none">{lockReason}</p>
        </div>
      )}
    </div>
  );
};

export default AssessmentItem;
